/**
 * BasesReferenciaPage — administração das bases de referência (M3).
 *
 * 🔴 Admin-only (RN-M3-002/003): a rota é protegida por ProtectedRoute(['admin']).
 * Lista as versões por tipo (GET /bases), permite iniciar nova versão INEP/IBGE
 * (UploadBaseModal → POST /bases/{tipo}) e editar os parâmetros de validação
 * (ParametrosValidacaoForm → GET/PATCH /parametros). Consome a API real (hooks).
 */

import { useState } from 'react';
import { useBasesReferencia } from '../hooks/useBasesReferencia';
import { UploadBaseModal } from '../components/UploadBaseModal';
import { ParametrosValidacaoForm } from '../components/ParametrosValidacaoForm';
import { TipoBaseUpload } from '../services/bases-types';

function formatarData(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
}

export function BasesReferenciaPage(): JSX.Element {
  const { bases, carregando, erro, recarregar } = useBasesReferencia();
  const [tipoUpload, setTipoUpload] = useState<TipoBaseUpload | null>(null);

  return (
    <div>
      <h1 className="page-title">Bases de Referência</h1>
      <p className="page-subtitle">
        Catálogos INEP, malha municipal IBGE e tabela de DDD usados pela validação. Restrito ao
        Administrador.
      </p>

      <section className="card">
        <div className="row-actions">
          <button type="button" className="btn btn-primary" onClick={() => setTipoUpload('inep')}>
            Nova versão INEP
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setTipoUpload('ibge')}>
            Nova versão IBGE
          </button>
        </div>
        <p className="muted">
          A tabela de DDD é um dado estático provisionado por seed — não há upload pela tela.
        </p>
      </section>

      <section className="card">
        <h2>Versões de base</h2>
        {carregando && <p className="muted">Carregando versões…</p>}
        {erro && <p className="notice">Não foi possível carregar as versões de base.</p>}
        {!carregando && !erro && bases.length === 0 && (
          <p className="muted">Nenhuma versão de base registrada ainda.</p>
        )}
        {!carregando && !erro && bases.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Versão</th>
                <th>Atualizada em</th>
                <th>Registros</th>
              </tr>
            </thead>
            <tbody>
              {bases.map((b) => (
                <tr key={`${b.tipo}-${b.versao}`}>
                  <td>{b.tipo}</td>
                  <td>{b.versao}</td>
                  <td>{formatarData(b.atualizadaEm)}</td>
                  <td>{b.registros.toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ParametrosValidacaoForm />

      {tipoUpload && (
        <UploadBaseModal
          tipo={tipoUpload}
          onFechar={() => setTipoUpload(null)}
          onIniciada={recarregar}
        />
      )}
    </div>
  );
}
